"use client"

import { useSyncExternalStore } from "react"

/**
 * What the player has actually encountered, which is what Encyclopelfia unlocks
 * against.
 *
 * Discovery is permanent and device-local: once you have built a tower, met an
 * enemy or held an item, its codex entry stays readable across runs. Nothing is
 * ever un-discovered, so this only grows.
 *
 * Kept separate from player-stats (scores) and player-prefs (settings) because
 * it is written from the hot path - every enemy spawn calls in here - and wants
 * a cheap "already known?" check rather than a full state read.
 */

export type CodexCategory = "towers" | "enemies" | "items"

export interface CodexDiscovery {
  towers: string[]
  enemies: string[]
  items: string[]
}

const STORAGE_KEY = "pelf-codex-discovery"

const EMPTY: CodexDiscovery = { towers: [], enemies: [], items: [] }

// Sets for O(1) membership; the serialized form is arrays so it stays readable
// in devtools and diffs cleanly.
const known: Record<CodexCategory, Set<string>> = {
  towers: new Set(),
  enemies: new Set(),
  items: new Set(),
}

let snapshot: CodexDiscovery = EMPTY
const listeners = new Set<() => void>()

const toSnapshot = (): CodexDiscovery => ({
  towers: [...known.towers],
  enemies: [...known.enemies],
  items: [...known.items],
})

function load(): void {
  if (typeof window === "undefined") return
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Partial<CodexDiscovery>
    for (const category of ["towers", "enemies", "items"] as const) {
      const values = parsed[category]
      if (Array.isArray(values)) {
        for (const value of values) {
          if (typeof value === "string" && value) known[category].add(value)
        }
      }
    }
    snapshot = toSnapshot()
  } catch {
    // Corrupt payload - start from empty rather than throwing during render
  }
}
load()

function persist(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
  } catch {
    // Private browsing / storage full - discovery just won't survive reload
  }
}

/**
 * Record an encounter. Returns true only when this was genuinely new, so hot
 * callers can skip the write and the re-render on every repeat sighting.
 */
export function discover(category: CodexCategory, id: string): boolean {
  if (!id || known[category].has(id)) return false
  known[category].add(id)
  snapshot = toSnapshot()
  persist()
  for (const listener of listeners) listener()
  return true
}

/** Record several at once, emitting a single update. */
export function discoverMany(category: CodexCategory, ids: readonly string[]): void {
  let changed = false
  for (const id of ids) {
    if (id && !known[category].has(id)) {
      known[category].add(id)
      changed = true
    }
  }
  if (!changed) return
  snapshot = toSnapshot()
  persist()
  for (const listener of listeners) listener()
}

export function isDiscovered(category: CodexCategory, id: string): boolean {
  return known[category].has(id)
}

export function getCodexDiscovery(): CodexDiscovery {
  return snapshot
}

export function resetCodexDiscovery(): void {
  known.towers.clear()
  known.enemies.clear()
  known.items.clear()
  snapshot = EMPTY
  persist()
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Reactive accessor for components (the codex grids). */
export function useCodexDiscovery(): CodexDiscovery {
  return useSyncExternalStore(subscribe, getCodexDiscovery, () => EMPTY)
}
